import { useState } from "react";

import { ChevronDown, ChevronRight, MoreHorizontal } from "lucide-react";
import { Link } from "react-router";

import { COMPONENT_DETAILS_PATHS } from "@/components/pages_logged_in/program/components/component-utils";
import { canEndEarlyComponent } from "@/components/pages_logged_in/program/components/componentMetadata";
import { getComponentItemStyle } from "@/components/pages_logged_in/program/components/program-timeline/utils";
import {
  allowEditComponent,
  getCloseDate,
  isCloseEarlyComponent,
} from "@/components/pages_logged_in/program/components/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ComponentTypeEnum } from "@/graphql";
import { cn } from "@/lib/utils";
import { LeadComponent, ParticipantComponent } from "@/types/programs";
import { HeaderConfig } from "@/types/tables";
import { formatDate } from "@/utils/datetime";

type ProgramComponentTableField =
  | "name"
  | "actions"
  | "targetStartDate"
  | "targetDueDate"
  | "targetEndDate";

interface ProgramHeaderConfig extends HeaderConfig {
  field: ProgramComponentTableField;
}

type ProgramComponent = (LeadComponent | ParticipantComponent) & {
  program?: LeadComponent["program"];
  parentComponent?: ParticipantComponent["parentComponent"];
};

interface TableColumnsProps {
  displayColumns: ProgramHeaderConfig[];
  row: ProgramComponent;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
  isLead?: boolean;
  isExpanded?: boolean;
  isChild?: boolean;
  onExpand?: (value: boolean) => void;
}

export function ComponentTableColumns({
  displayColumns,
  row,
  onEdit,
  onDelete,
  onClose,
  isLead,
  isExpanded,
  isChild,
  onExpand,
}: TableColumnsProps) {
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const dataColumns = displayColumns.map((column) => {
    const isGroupMeeting = row.type === ComponentTypeEnum.GroupMeeting;
    const couldEndEarly =
      isLead && canEndEarlyComponent(row.status, isGroupMeeting, isLead);
    const isProgramClosed = Boolean(row.program?.closedAt);
    let componentId = row.id;

    if (
      [
        ComponentTypeEnum.OneOneCoachingIntro,
        ComponentTypeEnum.OneOneCoachingSession,
      ].includes(row.type)
    ) {
      componentId = row.parentComponent?.id || "";
    }

    const isEditable = allowEditComponent({
      component: row,
      isProgramClosed,
      isProgramLead: isLead || false,
    });

    if (column.field === "name") {
      const item = getComponentItemStyle(row);
      return (
        <td
          key={column.field}
          className="border-none relative"
          style={{
            maxWidth: column.maxWidth || "auto",
          }}
        >
          <div className="flex cursor-pointer items-center gap-2.5 overflow-hidden">
            {row.childComponents?.length ? (
              isExpanded ? (
                <ChevronDown
                  className="h-3.5 w-3.5 cursor-pointer"
                  style={{ color: item.textColor }}
                  onClick={() => {
                    onExpand?.(false);
                  }}
                />
              ) : (
                <ChevronRight
                  className="h-3.5 w-3.5 cursor-pointer"
                  style={{ color: item.textColor }}
                  onClick={() => {
                    onExpand?.(true);
                  }}
                />
              )
            ) : null}
            {isLead ? (
              <Link
                className={cn({
                  "!ml-5.75": isChild,
                })}
                to={`/programs/${row.programId}/${
                  COMPONENT_DETAILS_PATHS[row.type]
                }/${componentId}`}
              >
                <div
                  className={cn(
                    "w-full flex items-center overflow-hidden select-none cursor-pointer",
                  )}
                >
                  <div className="mr-2">{item.icon}</div>
                  <div
                    className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium"
                    style={{ color: item.textColor }}
                  >
                    {row?.name}
                  </div>
                </div>
              </Link>
            ) : (
              <div
                className={cn({
                  "!ml-5.75": isChild,
                })}
              >
                <div className="w-full flex items-center overflow-hidden select-none">
                  <div className="mr-2">{item.icon}</div>
                  <div
                    className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium"
                    style={{ color: item.textColor }}
                  >
                    {row?.name}
                  </div>
                </div>
              </div>
            )}
          </div>
        </td>
      );
    }

    if (column.field === "actions" && isEditable) {
      return (
        <td key={column.field} className="flex justify-center">
          <DropdownMenu open={isOpenMenu} onOpenChange={setIsOpenMenu}>
            <DropdownMenuTrigger asChild>
              <Button
                intent="secondary"
                size="md"
                className="h-10 w-10"
                onClick={() => setIsOpenMenu(!isOpenMenu)}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  onEdit();
                  e.stopPropagation();
                  setIsOpenMenu(false);
                }}
              >
                Edit Component
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {couldEndEarly ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    setIsOpenMenu(false);
                  }}
                >
                  End Early
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                    setIsOpenMenu(false);
                  }}
                >
                  Delete Component
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      );
    }

    if (column.field === "actions") return null;

    if (column.field === "targetEndDate") {
      const isCloseEarly = isCloseEarlyComponent(row);
      const date = getCloseDate(row);
      const closedDate = date ? formatDate(date, "MMM d, yyyy h:mm a") : null;

      return (
        <td key={column.field}>
          {closedDate} {isCloseEarly ? " (ended early)" : ""}
        </td>
      );
    }

    if (column.field === "targetDueDate") {
      let displayDate = row?.targetDueDate;
      const isCloseEarly = isCloseEarlyComponent(row);

      return (
        <td key={column.field}>
          {formatDate(displayDate, "MMM d, yyyy h:mm a")}{" "}
          {isCloseEarly && !isLead ? " (ended early)" : ""}
        </td>
      );
    }

    const date =
      column.field === "targetStartDate"
        ? row.targetStartDate
        : row.targetDueDate;

    return <td key={column.field}>{formatDate(date, "MMM d, yyyy h:mm a")}</td>;
  });

  return dataColumns;
}
